import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const STEP_MS = 1000;
const SEQUENCE = [3, 2, 1] as const;

type CountdownOverlayProps = {
  isVisible: boolean;
  onComplete: () => void;
};

const digitAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(1.4);
  }
  20% {
    opacity: 1;
    transform: scale(1);
  }
  60% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0;
    transform: scale(0.85);
  }
`;

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const Digit = styled.span`
  font-size: 160px;
  font-weight: 800;
  color: white;
  text-shadow: 0 4px 24px rgba(0, 0, 0, 0.55);
  animation: ${digitAnimation} ${STEP_MS}ms ease-in-out forwards;
`;

export function CountdownOverlay({ isVisible, onComplete }: CountdownOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const onCompleteRef = useRef(onComplete);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setStepIndex(0);
    if (!isVisible) return;

    // Cap at SEQUENCE.length: that index renders nothing and marks the sequence
    // as finished, so the state stops changing once we run past the last digit.
    intervalRef.current = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, SEQUENCE.length));
    }, STEP_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [isVisible]);

  // Completion is a side effect, so it belongs in an effect rather than inside
  // the setStepIndex updater. React may invoke an updater more than once (it
  // always does under StrictMode), which made onComplete fire repeatedly.
  useEffect(() => {
    if (!isVisible || stepIndex < SEQUENCE.length) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onCompleteRef.current();
  }, [isVisible, stepIndex]);

  if (!isVisible) return null;

  const value = SEQUENCE[stepIndex];
  if (value === undefined) return null;

  return (
    <Wrapper aria-hidden="true">
      <Digit key={`${stepIndex}-${value}`}>{value}</Digit>
    </Wrapper>
  );
}

export default CountdownOverlay;
